import { core } from './client';
import type {
  AcademicPolicy,
  AcademicPolicyDraftRequest,
  ChangeEvent,
  FormDomain,
  FormField,
  FormSchema,
  FormSchemaDraftRequest,
  FormSection,
  GradeBand,
  GradingStrategy,
  GradingStrategyDraftRequest,
  HonoursRule,
  LifecycleStatus,
  PolicyDomain,
  ValidationReport,
} from '@/types/config-governance';

/**
 * The three configuration-governance modules' calls, in one file.
 *
 * ROUTING. All three controllers mount under PLATFORM_ADMIN_ROUTE_PREFIX on
 * campusone-core-service — the prefix resolves to 'platform', so the paths are
 * /platform/grading-strategies, /platform/academic-policies and
 * /platform/form-schemas behind the BFF's core upstream, exactly the mechanism
 * src/api/studio.ts uses for the Configuration Studio. The paths below are
 * transcribed from GradingStrategyAdminController, AcademicPolicyAdminController
 * and FormSchemaAdminController; test/governance-api.test.ts pins them so a
 * controller move fails a test here instead of becoming a permanently wrong
 * "not available yet" panel.
 *
 * SCOPE FILTER. Each list route takes institutionId as either an institution's
 * UUID or the literal string 'platform', which the controller translates to
 * "catalogue entries only" (institutionId IS NULL). The literal goes over the
 * wire as-is; this module does not re-encode the convention.
 *
 * VALIDATED AT THE BOUNDARY, like the studio module. A strategy whose bands are
 * not an array would reach the band-scale SVG and blank the page three renders
 * later, where the stack names a component instead of an endpoint. Malformed
 * bodies throw HERE, so AsyncPanel can say which call broke. Numeric columns are
 * numeric(8,3) in Postgres and the entity transformer keeps them numbers, but a
 * defensive Number() conversion costs nothing and survives a serializer change.
 */

const GRADING = '/platform/grading-strategies';
const POLICIES = '/platform/academic-policies';
const FORMS = '/platform/form-schemas';

const seg = encodeURIComponent;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function fail(what: string): never {
  throw new Error(`The server returned ${what} this app cannot read.`);
}

function num(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function optStr(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : [];
}

function post(payload: unknown): RequestInit {
  return { method: 'POST', body: JSON.stringify(payload) };
}

function patch(payload: unknown): RequestInit {
  return { method: 'PATCH', body: JSON.stringify(payload) };
}

/**
 * POST :id/validate and the 400 body of a refused activation share this shape:
 * { valid, errors[] }. The errors are the server's own sentences and are shown
 * verbatim — rewriting them here would desynchronise the words on screen from
 * the rule that produced them.
 */
function validationReport(payload: unknown): ValidationReport {
  if (!isRecord(payload) || !Array.isArray(payload.errors)) fail('a validation report');
  return { valid: payload.valid === true, errors: stringArray(payload.errors) };
}

// ── Grading strategies ─────────────────────────────────────────────────────

function band(value: unknown): GradeBand | null {
  if (!isRecord(value) || typeof value.code !== 'string') return null;
  return {
    code: value.code,
    label: str(value.label) || value.code,
    minInclusive: num(value.minInclusive),
    maxInclusive: num(value.maxInclusive),
    gradePoints:
      value.gradePoints === null || value.gradePoints === undefined ? null : num(value.gradePoints),
    description: optStr(value.description),
  };
}

function strategy(payload: unknown): GradingStrategy {
  if (
    !isRecord(payload) ||
    typeof payload.id !== 'string' ||
    typeof payload.code !== 'string' ||
    typeof payload.status !== 'string'
  ) {
    fail('a grading strategy');
  }
  return {
    id: payload.id,
    code: payload.code,
    name: str(payload.name) || payload.code,
    description: optStr(payload.description),
    gradingModel: str(payload.gradingModel) as GradingStrategy['gradingModel'],
    gradingScale: str(payload.gradingScale) as GradingStrategy['gradingScale'],
    minScore: num(payload.minScore),
    maxScore: num(payload.maxScore),
    passThreshold: num(payload.passThreshold),
    bands: Array.isArray(payload.bands)
      ? payload.bands.flatMap((entry) => {
          const parsed = band(entry);
          return parsed ? [parsed] : [];
        })
      : [],
    rounding: (str(payload.rounding) || 'NONE') as GradingStrategy['rounding'],
    decimalPlaces: typeof payload.decimalPlaces === 'number' ? payload.decimalPlaces : 2,
    honours: Array.isArray(payload.honours)
      ? payload.honours.flatMap((entry): HonoursRule[] =>
          isRecord(entry) && typeof entry.classification === 'string'
            ? [{ classification: entry.classification, minAverage: num(entry.minAverage) }]
            : [],
        )
      : null,
    missingResultRule: (str(payload.missingResultRule) ||
      'EXCLUDE') as GradingStrategy['missingResultRule'],
    reassessmentRule: isRecord(payload.reassessmentRule)
      ? (payload.reassessmentRule as Record<string, unknown>)
      : null,
    applicableInstitutionTypes: stringArray(payload.applicableInstitutionTypes),
    status: payload.status as GradingStrategy['status'],
    version: typeof payload.version === 'number' ? payload.version : 1,
    institutionId: optStr(payload.institutionId),
    createdAt: str(payload.createdAt),
    updatedAt: str(payload.updatedAt),
    createdBy: optStr(payload.createdBy),
    updatedBy: optStr(payload.updatedBy),
  };
}

function strategyList(payload: unknown): GradingStrategy[] {
  const rows = isRecord(payload) && Array.isArray(payload.strategies) ? payload.strategies : null;
  if (!rows) fail('a grading strategy list');
  // A junk row is dropped rather than sinking the list: the missing strategy
  // is conspicuous by absence, a blank error page explains nothing.
  return rows.flatMap((entry) => {
    try {
      return [strategy(entry)];
    } catch {
      return [];
    }
  });
}

export const gradingStrategiesApi = {
  list: (filter?: { status?: string; institutionId?: string }) => {
    const query = new URLSearchParams();
    if (filter?.status) query.set('status', filter.status);
    if (filter?.institutionId) query.set('institutionId', filter.institutionId);
    const qs = query.toString();
    return core<unknown>(`${GRADING}${qs ? `?${qs}` : ''}`).then(strategyList);
  },
  get: (id: string) => core<unknown>(`${GRADING}/${seg(id)}`).then(strategy),
  create: (body: GradingStrategyDraftRequest) =>
    core<unknown>(GRADING, post(body)).then(strategy),
  update: (id: string, body: Partial<GradingStrategyDraftRequest>) =>
    core<unknown>(`${GRADING}/${seg(id)}`, patch(body)).then(strategy),
  remove: (id: string) => core<void>(`${GRADING}/${seg(id)}`, { method: 'DELETE' }),
  validate: (id: string) =>
    core<unknown>(`${GRADING}/${seg(id)}/validate`, post({})).then(validationReport),
  activate: (id: string) =>
    core<unknown>(`${GRADING}/${seg(id)}/activate`, post({})).then(strategy),
};

// ── Shared lifecycle pieces (policies + form schemas) ──────────────────────

function changeEvent(value: unknown): ChangeEvent | null {
  if (!isRecord(value) || typeof value.toStatus !== 'string') return null;
  return {
    id: str(value.id),
    fromStatus: optStr(value.fromStatus) as ChangeEvent['fromStatus'],
    toStatus: value.toStatus as LifecycleStatus,
    actor: str(value.actor),
    reason: optStr(value.reason),
    at: str(value.at),
  };
}

function eventList(payload: unknown): ChangeEvent[] {
  const rows = isRecord(payload) && Array.isArray(payload.events) ? payload.events : null;
  if (!rows) fail('a change history');
  return rows.flatMap((entry) => {
    const parsed = changeEvent(entry);
    return parsed ? [parsed] : [];
  });
}

// ── Academic policies ──────────────────────────────────────────────────────

function policy(payload: unknown): AcademicPolicy {
  if (
    !isRecord(payload) ||
    typeof payload.id !== 'string' ||
    typeof payload.policyDomain !== 'string' ||
    typeof payload.status !== 'string'
  ) {
    fail('an academic policy');
  }
  return {
    id: payload.id,
    policyDomain: payload.policyDomain as AcademicPolicy['policyDomain'],
    title: str(payload.title) || payload.policyDomain,
    description: optStr(payload.description),
    version: typeof payload.version === 'number' ? payload.version : 1,
    status: payload.status as LifecycleStatus,
    effectiveFrom: str(payload.effectiveFrom),
    applicableInstitutionTypes: stringArray(payload.applicableInstitutionTypes),
    institutionId: optStr(payload.institutionId),
    rules: isRecord(payload.rules) ? (payload.rules as Record<string, unknown>) : {},
    exceptions: Array.isArray(payload.exceptions)
      ? (payload.exceptions.filter(isRecord) as Record<string, unknown>[])
      : null,
    approvedBy: optStr(payload.approvedBy),
    approvedAt: optStr(payload.approvedAt),
    changeSummary: optStr(payload.changeSummary),
    lastTransitionReason: optStr(payload.lastTransitionReason),
    createdAt: str(payload.createdAt),
    updatedAt: str(payload.updatedAt),
    createdBy: optStr(payload.createdBy),
    updatedBy: optStr(payload.updatedBy),
  };
}

function policyList(payload: unknown): AcademicPolicy[] {
  const rows = isRecord(payload) && Array.isArray(payload.policies) ? payload.policies : null;
  if (!rows) fail('an academic policy list');
  return rows.flatMap((entry) => {
    try {
      return [policy(entry)];
    } catch {
      return [];
    }
  });
}

export const academicPoliciesApi = {
  list: (filter?: { policyDomain?: PolicyDomain; status?: string; institutionId?: string }) => {
    const query = new URLSearchParams();
    if (filter?.policyDomain) query.set('policyDomain', filter.policyDomain);
    if (filter?.status) query.set('status', filter.status);
    if (filter?.institutionId) query.set('institutionId', filter.institutionId);
    const qs = query.toString();
    return core<unknown>(`${POLICIES}${qs ? `?${qs}` : ''}`).then(policyList);
  },
  get: (id: string) => core<unknown>(`${POLICIES}/${seg(id)}`).then(policy),
  history: (id: string) => core<unknown>(`${POLICIES}/${seg(id)}/history`).then(eventList),
  create: (body: AcademicPolicyDraftRequest) => core<unknown>(POLICIES, post(body)).then(policy),
  update: (id: string, body: Partial<AcademicPolicyDraftRequest>) =>
    core<unknown>(`${POLICIES}/${seg(id)}`, patch(body)).then(policy),
  remove: (id: string) => core<void>(`${POLICIES}/${seg(id)}`, { method: 'DELETE' }),
  transition: (id: string, toStatus: LifecycleStatus, reason?: string) =>
    core<unknown>(
      `${POLICIES}/${seg(id)}/transition`,
      post(reason ? { toStatus, reason } : { toStatus }),
    ).then(policy),
};

// ── Form schemas ───────────────────────────────────────────────────────────

function conditional(value: unknown): FormField['conditionalOn'] {
  if (!isRecord(value) || typeof value.fieldCode !== 'string') return undefined;
  const equals = value.equals;
  if (typeof equals !== 'string' && typeof equals !== 'number' && typeof equals !== 'boolean') {
    return undefined;
  }
  return { fieldCode: value.fieldCode, equals };
}

function section(value: unknown): FormSection | null {
  if (!isRecord(value) || typeof value.code !== 'string') return null;
  const rule = conditional(value.conditionalOn);
  return {
    code: value.code,
    title: str(value.title) || value.code,
    ...(typeof value.description === 'string' ? { description: value.description } : {}),
    ordinal: num(value.ordinal),
    ...(rule ? { conditionalOn: rule } : {}),
  };
}

function field(value: unknown): FormField | null {
  if (!isRecord(value) || typeof value.code !== 'string' || typeof value.fieldType !== 'string') {
    return null;
  }
  const rule = conditional(value.conditionalOn);
  const validation = isRecord(value.validation)
    ? {
        ...(typeof value.validation.minLength === 'number'
          ? { minLength: value.validation.minLength }
          : {}),
        ...(typeof value.validation.maxLength === 'number'
          ? { maxLength: value.validation.maxLength }
          : {}),
        ...(typeof value.validation.min === 'number' ? { min: value.validation.min } : {}),
        ...(typeof value.validation.max === 'number' ? { max: value.validation.max } : {}),
        ...(typeof value.validation.pattern === 'string'
          ? { pattern: value.validation.pattern }
          : {}),
        ...(Array.isArray(value.validation.allowedValues)
          ? { allowedValues: stringArray(value.validation.allowedValues) }
          : {}),
      }
    : undefined;
  const documentSpec = isRecord(value.documentSpec)
    ? {
        acceptedTypes: stringArray(value.documentSpec.acceptedTypes),
        maxSizeMb: num(value.documentSpec.maxSizeMb),
      }
    : undefined;
  return {
    code: value.code,
    sectionCode: str(value.sectionCode),
    label: str(value.label) || value.code,
    fieldType: value.fieldType as FormField['fieldType'],
    ordinal: num(value.ordinal),
    required: value.required === true,
    protected: value.protected === true,
    sensitivity: (str(value.sensitivity) || 'INTERNAL') as FormField['sensitivity'],
    ...(rule ? { conditionalOn: rule } : {}),
    ...(validation && Object.keys(validation).length > 0 ? { validation } : {}),
    ...(documentSpec ? { documentSpec } : {}),
    ...(Array.isArray(value.roleVisibility)
      ? { roleVisibility: stringArray(value.roleVisibility) }
      : {}),
    ...(value.defaultValue !== undefined && value.defaultValue !== null
      ? typeof value.defaultValue === 'string' ||
        typeof value.defaultValue === 'number' ||
        typeof value.defaultValue === 'boolean'
        ? { defaultValue: value.defaultValue }
        : {}
      : {}),
    ...(typeof value.helpText === 'string' ? { helpText: value.helpText } : {}),
  };
}

function schema(payload: unknown): FormSchema {
  if (
    !isRecord(payload) ||
    typeof payload.id !== 'string' ||
    typeof payload.formDomain !== 'string' ||
    typeof payload.status !== 'string'
  ) {
    fail('a form schema');
  }
  return {
    id: payload.id,
    formDomain: payload.formDomain as FormSchema['formDomain'],
    code: str(payload.code),
    title: str(payload.title) || str(payload.code),
    description: optStr(payload.description),
    version: typeof payload.version === 'number' ? payload.version : 1,
    status: payload.status as LifecycleStatus,
    effectiveFrom: str(payload.effectiveFrom),
    applicableInstitutionTypes: stringArray(payload.applicableInstitutionTypes),
    institutionId: optStr(payload.institutionId),
    baseSchemaId: optStr(payload.baseSchemaId),
    sections: Array.isArray(payload.sections)
      ? payload.sections.flatMap((entry) => {
          const parsed = section(entry);
          return parsed ? [parsed] : [];
        })
      : [],
    fields: Array.isArray(payload.fields)
      ? payload.fields.flatMap((entry) => {
          const parsed = field(entry);
          return parsed ? [parsed] : [];
        })
      : [],
    approvedBy: optStr(payload.approvedBy),
    approvedAt: optStr(payload.approvedAt),
    changeSummary: optStr(payload.changeSummary),
    lastTransitionReason: optStr(payload.lastTransitionReason),
    createdAt: str(payload.createdAt),
    updatedAt: str(payload.updatedAt),
    createdBy: optStr(payload.createdBy),
    updatedBy: optStr(payload.updatedBy),
  };
}

function schemaList(payload: unknown): FormSchema[] {
  const rows = isRecord(payload) && Array.isArray(payload.schemas) ? payload.schemas : null;
  if (!rows) fail('a form schema list');
  return rows.flatMap((entry) => {
    try {
      return [schema(entry)];
    } catch {
      return [];
    }
  });
}

export const formSchemasApi = {
  list: (filter?: { formDomain?: FormDomain; status?: string; institutionId?: string }) => {
    const query = new URLSearchParams();
    if (filter?.formDomain) query.set('formDomain', filter.formDomain);
    if (filter?.status) query.set('status', filter.status);
    if (filter?.institutionId) query.set('institutionId', filter.institutionId);
    const qs = query.toString();
    return core<unknown>(`${FORMS}${qs ? `?${qs}` : ''}`).then(schemaList);
  },
  get: (id: string) => core<unknown>(`${FORMS}/${seg(id)}`).then(schema),
  history: (id: string) => core<unknown>(`${FORMS}/${seg(id)}/history`).then(eventList),
  create: (body: FormSchemaDraftRequest) => core<unknown>(FORMS, post(body)).then(schema),
  update: (id: string, body: Partial<FormSchemaDraftRequest>) =>
    core<unknown>(`${FORMS}/${seg(id)}`, patch(body)).then(schema),
  remove: (id: string) => core<void>(`${FORMS}/${seg(id)}`, { method: 'DELETE' }),
  validate: (id: string) =>
    core<unknown>(`${FORMS}/${seg(id)}/validate`, post({})).then(validationReport),
  transition: (id: string, toStatus: LifecycleStatus, reason?: string) =>
    core<unknown>(
      `${FORMS}/${seg(id)}/transition`,
      post(reason ? { toStatus, reason } : { toStatus }),
    ).then(schema),
};

/**
 * The institution-admin twin of the same register.
 *
 * ROUTING. These mount on FormSchemaInstitutionAdminController, which serves
 * /admin/institution/:institutionId/form-schemas behind the BFF's core
 * upstream — the same prefix the institution console's other admin.* calls
 * use. Every call carries the institution as a path segment (taken from the
 * route, never from the body) and every response is scoped to that
 * institution. `includePlatformTemplates` is the one surface the platform
 * register does not expose as a flag: it asks the controller to append the
 * ACTIVE platform templates alongside this institution's own versions, so the
 * institution console can offer them as derivation bases.
 */
export const institutionFormSchemasApi = {
  list: (
    institutionId: string,
    filter?: {
      formDomain?: FormDomain;
      status?: string;
      includePlatformTemplates?: boolean;
    },
  ) => {
    const query = new URLSearchParams();
    if (filter?.formDomain) query.set('formDomain', filter.formDomain);
    if (filter?.status) query.set('status', filter.status);
    if (filter?.includePlatformTemplates) query.set('includePlatformTemplates', 'true');
    const qs = query.toString();
    const base = `/admin/institution/${seg(institutionId)}/form-schemas`;
    return core<unknown>(`${base}${qs ? `?${qs}` : ''}`).then(schemaList);
  },
  get: (institutionId: string, id: string) =>
    core<unknown>(`/admin/institution/${seg(institutionId)}/form-schemas/${seg(id)}`).then(schema),
  history: (institutionId: string, id: string) =>
    core<unknown>(
      `/admin/institution/${seg(institutionId)}/form-schemas/${seg(id)}/history`,
    ).then(eventList),
  create: (institutionId: string, body: FormSchemaDraftRequest) =>
    core<unknown>(`/admin/institution/${seg(institutionId)}/form-schemas`, post(body)).then(
      schema,
    ),
  update: (institutionId: string, id: string, body: Partial<FormSchemaDraftRequest>) =>
    core<unknown>(
      `/admin/institution/${seg(institutionId)}/form-schemas/${seg(id)}`,
      patch(body),
    ).then(schema),
  remove: (institutionId: string, id: string) =>
    core<void>(`/admin/institution/${seg(institutionId)}/form-schemas/${seg(id)}`, {
      method: 'DELETE',
    }),
  validate: (institutionId: string, id: string) =>
    core<unknown>(
      `/admin/institution/${seg(institutionId)}/form-schemas/${seg(id)}/validate`,
      post({}),
    ).then(validationReport),
  transition: (institutionId: string, id: string, toStatus: LifecycleStatus, reason?: string) =>
    core<unknown>(
      `/admin/institution/${seg(institutionId)}/form-schemas/${seg(id)}/transition`,
      post(reason ? { toStatus, reason } : { toStatus }),
    ).then(schema),
};
